import OpenAI from 'openai'
import { openaiEnv } from './env.js'

/**
 * OpenAI API Client Configuration
 *
 * SECURITY NOTICE:
 * This file configures how the application communicates with OpenAI APIs.
 * To enhance security:
 *
 * 1. In production, all API calls go through our backend proxy
 * 2. The dangerouslyAllowBrowser flag is completely removed
 * 3. API keys are never exposed in client-side code
 */

// Environment detection
const isProduction = process.env.NODE_ENV === 'production'
const API_URL = isProduction
  ? '/api/openai'
  : 'http://localhost:5000/api/openai'

/**
 * Safe OpenAI API access for both production and development
 *
 * In production, this will send all requests through the backend proxy
 * In development, you can choose to use the proxy or direct access based on the PROXY_IN_DEV env var
 */
export const callOpenAIAPI = async (endpoint, data, options = {}) => {
  // Auth headers - in production will use Firebase auth token
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  }

  // Add authorization if available
  if (options.authToken) {
    headers['Authorization'] = `Bearer ${options.authToken}`
  }

  // Configuration for the fetch request
  const fetchOptions = {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
    credentials: 'include', // Include cookies for auth sessions
  }

  try {
    // Make the API call through the proxy
    const response = await fetch(`${API_URL}/${endpoint}`, fetchOptions)

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `API error: ${response.status}`)
    }

    // Return the stream response for processing
    return response
  } catch (error) {
    console.error('Error calling OpenAI API:', error)
    throw error
  }
}

/**
 * Chat completion API wrapper - uses the safe proxy
 * This replaces the direct OpenAI client usage
 */
export const createChatCompletion = async (messages, options = {}) => {
  const params = {
    messages,
    model: options.model || 'gpt-3.5-turbo',
    temperature: options.temperature || 0.7,
    max_tokens: options.max_tokens || 1000,
    stream: options.stream || true,
  }

  return callOpenAIAPI('chat', params, {
    authToken: options.authToken,
  })
}

/**
 * Legacy client for development compatibility
 * @deprecated Use createChatCompletion instead
 */
export const openai = {
  chat: {
    completions: {
      create: async (params) => {
        console.warn(
          'Deprecated: Direct OpenAI client usage is not secure. Use createChatCompletion instead.'
        )

        // Only allowed in development, and even then we encourage the proxy approach
        if (isProduction) {
          throw new Error(
            'Direct OpenAI API access is not allowed in production'
          )
        }

        // Create a proper client with no dangerous browser setting
        // This is for compatibility but should be avoided
        const client = new OpenAI({
          apiKey: openaiEnv.apiKey,
        })

        return client.chat.completions.create(params)
      },
    },
  },
}
